import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bus, Truck, Package, Car, ChevronRight, TrendingUp, Leaf, Euro,
  BarChart3, ArrowLeft, Download, Fuel, AlertTriangle, UserX, Receipt,
  Wrench, RefreshCw, Building2, User, Zap, Check, Target, Calendar,
  Award, ArrowRight, Loader2, FileText, Mail, Phone, Shield, Monitor,
  Gauge, TreePine, ClipboardList, ChevronDown, Save
} from "lucide-react";
import ComoLoHacemos from "./ComoLoHacemos";
import { trackFunnelEvent } from "../../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// IMPL-034 Fase 1d (2026-06-30) · Logo MOBILSAFE en headers verde gradient
// Variante blanca: img SVG (sin filter) + fallback con leaf en círculo blanco/glass + texto blanco
const MOBILSAFE_LOGO_URL = "/logo-mobilsafe.png";
const MobilsafeLogoOnGreen = () => (
  <>
    <img
      src={MOBILSAFE_LOGO_URL}
      alt="MOBILSAFE"
      className="h-9 md:h-10 bg-white/95 rounded px-2 py-1"
      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
    />
    <div className="hidden items-center gap-2">
      <div className="w-9 h-9 bg-white/15 backdrop-blur rounded-lg flex items-center justify-center border border-white/30">
        <Leaf className="w-5 h-5 text-white" />
      </div>
      <div className="leading-tight">
        <p className="text-base font-black tracking-tight text-white">MOBILSAFE</p>
        <p className="text-[9px] text-emerald-100 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
      </div>
    </div>
  </>
);

const Simulator = ({ config, clientData, diagnostico, onBack }) => {
  const [page, setPage] = useState('form');
  const [formData, setFormData] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [comparativaData, setComparativaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (config.presets && clientData.sector) {
      const preset = config.presets[clientData.sector];
      if (preset) {
        setFormData({
          clientName: clientData.empresa, sector: clientData.sector,
          numVehicles: preset.numVehicles, kmPerVehicleYear: preset.kmPerVehicleYear,
          fuelConsumption: preset.fuelConsumption, fuelCostPerLiter: preset.fuelCostPerLiter,
          accidentHoursPercent: preset.accidentHoursPercent, absenteeismPercent: preset.absenteeismPercent,
          finesPerVehicleYear: preset.finesPerVehicleYear, avgFineAmount: preset.avgFineAmount,
          maintenanceCostPerVehicle: preset.maintenanceCostPerVehicle, fuelSavingPercent: preset.fuelSavingPercent,
          accidentReductionPercent: preset.accidentReductionPercent, absenteeismReductionPercent: preset.absenteeismReductionPercent,
          fineReductionPercent: preset.fineReductionPercent, maintenanceReductionPercent: preset.maintenanceReductionPercent,
        });
      }
    }
  }, [config.presets, clientData]);

  const sectorIcons = { urbano: <Bus className="w-5 h-5" />, interurbano: <Bus className="w-5 h-5" />, distribucion: <Package className="w-5 h-5" />, logistica: <Truck className="w-5 h-5" />, taxi: <Car className="w-5 h-5" /> };

  const aplicarPreset = (sector) => {
    const preset = config.presets[sector];
    if (!preset) return;
    setFormData({ ...formData, sector, numVehicles: preset.numVehicles, kmPerVehicleYear: preset.kmPerVehicleYear, fuelConsumption: preset.fuelConsumption, fuelCostPerLiter: preset.fuelCostPerLiter, accidentHoursPercent: preset.accidentHoursPercent, absenteeismPercent: preset.absenteeismPercent, finesPerVehicleYear: preset.finesPerVehicleYear, avgFineAmount: preset.avgFineAmount, maintenanceCostPerVehicle: preset.maintenanceCostPerVehicle, fuelSavingPercent: preset.fuelSavingPercent, accidentReductionPercent: preset.accidentReductionPercent, absenteeismReductionPercent: preset.absenteeismReductionPercent, fineReductionPercent: preset.fineReductionPercent, maintenanceReductionPercent: preset.maintenanceReductionPercent });
    toast.success(`Valores cargados: ${preset.nombre}`);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({ ...formData, [name]: type === 'number' ? parseFloat(value) : value });
  };

  const calcularImpacto = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calcular`, formData);
      setResultado(response.data);
      const bench = config.benchmarks?.[formData.sector];
      setComparativaData({ bench, formData: { ...formData } });
      // IMPL-055 · cálculo completado con éxito
      trackFunnelEvent("simulator", "simulacion_completada", { sector: formData.sector });
      setPage('comparativa');
    } catch (error) { toast.error('Error al calcular'); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/simulaciones`, {
        empresa: clientData.empresa, contacto: clientData.contacto,
        emailCliente: clientData.email, telefono: clientData.telefono,
        sector: clientData.sector, comercialId: clientData.comercialId, input: formData,
        tipoAnalisis: clientData.tipoAnalisis || 'rapido',
        diagnostico: diagnostico || null
      });
      toast.success('Simulación guardada');
      setSaved(true);
    } catch (error) { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const handlePrintIndividual = () => {
    setShowPdfMenu(false);
    // IMPL-055 · PDF generado (paso final del funnel)
    trackFunnelEvent("simulator", "pdf_generado", { tipo: "individual", sector: formData?.sector });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('El navegador bloqueó la ventana. Permite los pop-ups.');
      return;
    }
    const r = resultado || {};
    const bench = config.benchmarks?.[formData.sector] || {};
    const fmt = (v) => v != null ? Math.round(Number(v)).toLocaleString('es-ES') : '-';
    const fmt1 = (v) => v != null ? Number(v).toLocaleString('es-ES', { maximumFractionDigits: 1 }) : '-';
    const total = Number(r.totalAnnualSavingEuros || 0);
    const numVeh = Number(formData.numVehicles || 0);
    const inversion = numVeh * 1500;
    const monthlySaving = total / 12;
    const paybackMonths = monthlySaving > 0 ? Math.max(1, Math.round(inversion / monthlySaving)) : null;

    // Agregados por categoría
    const combustibleAhorro = Number(r.fuelSavingEuros || 0);
    const mantenimientoAhorro = Number(r.maintenanceSavingEuros || 0);
    const siniestralidadAhorro = Number(r.accidentSavingEuros || 0) + Number(r.absenteeismSavingEuros || 0) + Number(r.fineSavingEuros || 0);
    const caesAhorro = Number(r.caesNeto || 0);

    // Oportunidades dinámicas (1ª y 2ª por mayor ahorro; CAES siempre 3ª si caesNeto > 0)
    const candidatos = [
      {
        key: 'combustible',
        euros: combustibleAhorro,
        titulo: 'Optimización del combustible',
        texto: `Reducir un ${formData.fuelSavingPercent || 0}% el consumo (hoy ${formData.fuelConsumption} L/100km) libera <strong>${fmt(combustibleAhorro)} €/año</strong> en una flota de ${numVeh} vehículos.`,
      },
      {
        key: 'siniestralidad',
        euros: siniestralidadAhorro,
        titulo: 'Reducción de siniestralidad',
        texto: `Bajar accidentes (-${formData.accidentReductionPercent || 0}%), absentismo (-${formData.absenteeismReductionPercent || 0}%) y multas (-${formData.fineReductionPercent || 0}%) con telemática preventiva recupera <strong>${fmt(siniestralidadAhorro)} €/año</strong>.`,
      },
      {
        key: 'mantenimiento',
        euros: mantenimientoAhorro,
        titulo: 'Mantenimiento predictivo',
        texto: `Optimizar un ${formData.maintenanceReductionPercent || 0}% sobre ${fmt(formData.maintenanceCostPerVehicle)} €/vehículo/año reduce <strong>${fmt(mantenimientoAhorro)} €/año</strong> de coste operativo.`,
      },
    ];
    const orden = [...candidatos].sort((a, b) => b.euros - a.euros);
    const caesOportunidad = {
      key: 'caes',
      euros: caesAhorro,
      titulo: 'Ingresos CAES extra',
      texto: `Tu flota genera derechos CAES por valor neto de <strong>${fmt(caesAhorro)} €/año</strong> compensables vía Certificados de Ahorro Energético.`,
    };
    const oportunidades = caesAhorro > 0
      ? [orden[0], orden[1], caesOportunidad]
      : [orden[0], orden[1], orden[2]];

    // KPIs flota vs sector (5 filas)
    const kpis = [
      { label: 'Consumo medio', tuyo: `${fmt1(formData.fuelConsumption)} L/100km`, media: bench.consumo?.media != null ? `${fmt1(bench.consumo.media)} L/100km` : '—', peor: bench.consumo?.media != null && Number(formData.fuelConsumption) > Number(bench.consumo.media) },
      { label: 'Horas por accidentes', tuyo: `${fmt1(formData.accidentHoursPercent)} %`, media: bench.accidentes?.media != null ? `${fmt1(bench.accidentes.media)} %` : '—', peor: bench.accidentes?.media != null && Number(formData.accidentHoursPercent) > Number(bench.accidentes.media) },
      { label: 'Absentismo', tuyo: `${fmt1(formData.absenteeismPercent)} %`, media: bench.absentismo?.media != null ? `${fmt1(bench.absentismo.media)} %` : '—', peor: bench.absentismo?.media != null && Number(formData.absenteeismPercent) > Number(bench.absentismo.media) },
      { label: 'Multas por vehículo', tuyo: `${fmt1(formData.finesPerVehicleYear)} /año`, media: bench.multas?.media != null ? `${fmt1(bench.multas.media)} /año` : '—', peor: bench.multas?.media != null && Number(formData.finesPerVehicleYear) > Number(bench.multas.media) },
      { label: 'Mantenimiento', tuyo: `${fmt(formData.maintenanceCostPerVehicle)} €`, media: bench.mantenimiento?.media != null ? `${fmt(bench.mantenimiento.media)} €` : '—', peor: bench.mantenimiento?.media != null && Number(formData.maintenanceCostPerVehicle) > Number(bench.mantenimiento.media) },
    ];

    // Barras desglose proporcional
    const barras = [
      { label: 'Combustible', valor: combustibleAhorro, color: '#0a6640' },
      { label: 'Mantenimiento', valor: mantenimientoAhorro, color: '#15803d' },
      { label: 'Siniestralidad', valor: siniestralidadAhorro, color: '#22c55e' },
      { label: 'CAES', valor: caesAhorro, color: '#7c3aed' },
    ];
    const maxBarra = Math.max(...barras.map(b => b.valor), 1);

    const fecha = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
    const sectorName = bench.name || formData.sector || '—';
    const ctaUrl = `https://wa.me/34648671833?text=${encodeURIComponent(`Hola, soy ${clientData.contacto || ''} de ${clientData.empresa || ''}. Quiero ver el análisis completo de ahorro para mi flota.`)}`;

    printWindow.document.write(`
<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"><title>Informe MOBILSAFE - ${clientData.empresa}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #1f2937; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 210mm; min-height: 297mm; padding: 0; }
  /* HEADER */
  .header { background: #0a6640; color: white; padding: 14px 24px; display: flex; justify-content: space-between; align-items: center; }
  .logo { display: flex; align-items: center; gap: 10px; font-size: 22px; font-weight: 900; letter-spacing: 0.5px; }
  .logo svg { width: 26px; height: 26px; }
  .header-meta { text-align: right; font-size: 11px; line-height: 1.5; opacity: 0.95; }
  .header-meta strong { font-size: 13px; font-weight: 700; }
  /* HERO */
  .hero { background: #f0fdf4; padding: 22px 24px 18px; border-bottom: 1px solid #d1fae5; }
  .hero-label { text-transform: uppercase; font-size: 11px; letter-spacing: 1.5px; color: #047857; font-weight: 700; margin-bottom: 6px; }
  .hero-amount { font-size: 56px; font-weight: 900; color: #0a6640; line-height: 1; letter-spacing: -1px; }
  .hero-amount small { font-size: 28px; margin-left: 4px; }
  .hero-sub { font-size: 12px; color: #4b5563; margin-top: 6px; }
  .hero-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-top: 14px; }
  .hero-card { background: white; border: 1px solid #d1fae5; border-radius: 8px; padding: 10px 12px; }
  .hero-card-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  .hero-card-value { font-size: 18px; font-weight: 800; color: #0a6640; margin-top: 2px; }
  /* BODY 2 COLS */
  .body { display: grid; grid-template-columns: 1.15fr 1fr; gap: 18px; padding: 18px 24px; }
  .col h2 { font-size: 14px; font-weight: 800; color: #0a6640; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
  .opp { display: flex; gap: 12px; margin-bottom: 14px; }
  .opp-num { flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%; background: #0a6640; color: white; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; }
  .opp-body { flex: 1; }
  .opp-title { font-size: 13px; font-weight: 700; color: #0a6640; margin-bottom: 3px; }
  .opp-text { font-size: 11.5px; line-height: 1.5; color: #374151; }
  /* TABLA KPIs */
  .kpi-table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
  .kpi-table thead th { text-align: left; padding: 8px 8px; background: #f9fafb; font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: #6b7280; border-bottom: 2px solid #e5e7eb; }
  .kpi-table thead th:last-child, .kpi-table tbody td:last-child { text-align: right; }
  .kpi-table thead th:nth-child(2), .kpi-table tbody td:nth-child(2) { text-align: right; }
  .kpi-table tbody td { padding: 9px 8px; border-bottom: 1px solid #f3f4f6; }
  .kpi-table tbody tr:last-child td { border-bottom: none; }
  .kpi-label { color: #374151; font-weight: 500; }
  .val-peor { color: #dc2626; font-weight: 700; }
  .val-bueno { color: #0a6640; font-weight: 700; }
  .val-media { color: #6b7280; font-weight: 600; }
  /* DESGLOSE BARRAS */
  .desglose { padding: 4px 24px 16px; }
  .desglose h2 { font-size: 14px; font-weight: 800; color: #0a6640; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
  .barra-row { display: grid; grid-template-columns: 130px 1fr 100px; align-items: center; gap: 12px; margin-bottom: 8px; }
  .barra-label { font-size: 12px; color: #374151; font-weight: 600; }
  .barra-track { background: #f3f4f6; border-radius: 6px; height: 22px; overflow: hidden; }
  .barra-fill { height: 100%; border-radius: 6px; }
  .barra-valor { font-size: 12px; font-weight: 800; color: #0a6640; text-align: right; }
  /* FOOTER */
  .footer { background: #f9fafb; border-top: 1px solid #e5e7eb; padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
  .cta { background: #0a6640; color: white; padding: 12px 22px; border-radius: 8px; text-decoration: none; font-weight: 700; font-size: 13px; display: inline-flex; align-items: center; gap: 8px; }
  .cta:hover { background: #084d30; }
  .footer-info { font-size: 11px; color: #4b5563; text-align: right; line-height: 1.5; }
  .footer-info strong { color: #0a6640; }
  .legal { font-size: 9.5px; color: #9ca3af; padding: 6px 24px 10px; text-align: center; }
  @media print {
    body { margin: 0; }
    .page { page-break-after: avoid; }
  }
</style></head><body>
<div class="page">
  <div class="header">
    <div class="logo">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.2 2.96c.95 6.5 1.05 12.1-.5 15.04A7 7 0 0 1 11 20z"></path><path d="M2 21c0-3 1.85-5.36 5.08-6"></path></svg>
      MOBILSAFE
    </div>
    <div class="header-meta">
      <strong>${clientData.empresa || '—'}</strong><br>
      ${numVeh} vehículos · ${sectorName}<br>
      Generado el ${fecha}
    </div>
  </div>

  <div class="hero">
    <div class="hero-label">Ahorro anual estimado para tu flota</div>
    <div class="hero-amount">${fmt(total)}<small> €</small></div>
    <div class="hero-sub">Basado en los datos que has introducido${paybackMonths ? ` · recuperación de inversión en <strong>${paybackMonths} meses</strong>` : ''}</div>
    <div class="hero-grid">
      <div class="hero-card"><div class="hero-card-label">Ahorro combustible</div><div class="hero-card-value">${fmt(combustibleAhorro)} €</div></div>
      <div class="hero-card"><div class="hero-card-label">Menos accidentes y multas</div><div class="hero-card-value">${fmt(siniestralidadAhorro)} €</div></div>
      <div class="hero-card"><div class="hero-card-label">Ingresos CAES extra</div><div class="hero-card-value">${fmt(caesAhorro)} €</div></div>
    </div>
  </div>

  <div class="body">
    <div class="col">
      <h2>3 oportunidades detectadas</h2>
      ${oportunidades.map((o, i) => `
        <div class="opp">
          <div class="opp-num">${i + 1}</div>
          <div class="opp-body">
            <div class="opp-title">${o.titulo}</div>
            <div class="opp-text">${o.texto}</div>
          </div>
        </div>
      `).join('')}
    </div>
    <div class="col">
      <h2>Tu flota vs sector</h2>
      <table class="kpi-table">
        <thead><tr><th>KPI</th><th>Tu flota</th><th>Media sector</th></tr></thead>
        <tbody>
          ${kpis.map(k => `
            <tr>
              <td class="kpi-label">${k.label}</td>
              <td class="${k.peor ? 'val-peor' : 'val-bueno'}">${k.tuyo}</td>
              <td class="val-media">${k.media}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  </div>

  <div class="desglose">
    <h2>Desglose de ahorro anual</h2>
    ${barras.map(b => `
      <div class="barra-row">
        <div class="barra-label">${b.label}</div>
        <div class="barra-track"><div class="barra-fill" style="width: ${Math.max(2, (b.valor / maxBarra) * 100)}%; background: ${b.color};"></div></div>
        <div class="barra-valor">${fmt(b.valor)} €</div>
      </div>
    `).join('')}
  </div>

  <div class="footer">
    <a href="${ctaUrl}" class="cta" target="_blank" rel="noopener">Quiero ver el análisis completo →</a>
    <div class="footer-info">
      <strong>Equipo MOBILSAFE</strong><br>
      WhatsApp · <strong>648 671 833</strong>
    </div>
  </div>

  <div class="legal">
    Estimación basada en los datos introducidos y comparativa con ${bench.sampleSize || '—'} flotas similares en España. Valores indicativos, no contractuales.
  </div>
</div>
</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const handlePrintComparativa = () => {
    setShowPdfMenu(false);
    // IMPL-055 · PDF generado (paso final del funnel)
    trackFunnelEvent("simulator", "pdf_generado", { tipo: "comparativa", sector: formData?.sector });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('El navegador bloqueó la ventana. Permite los pop-ups.');
      return;
    }
    const bench = config.benchmarks?.[formData.sector];
    const r = resultado || {};
    const fmt = (v) => v != null ? Number(v).toLocaleString('es-ES') : '-';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Informe MOBILSAFE - ${clientData.empresa}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; color: #1f2937; }
        .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #10b981; }
        .logo { font-size: 28px; font-weight: 900; color: #10b981; }
        .subtitle { color: #6b7280; font-size: 14px; }
        .section { margin: 30px 0; page-break-inside: avoid; }
        .section-title { font-size: 20px; font-weight: 700; color: #10b981; margin-bottom: 15px; border-bottom: 2px solid #d1fae5; padding-bottom: 10px; }
        .hero { background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 40px; border-radius: 16px; text-align: center; margin: 20px 0; }
        .hero h1 { font-size: 48px; margin: 0 0 10px 0; }
        .hero p { font-size: 18px; opacity: 0.9; }
        .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin: 20px 0; }
        .card { background: #f0fdf4; padding: 20px; border-radius: 12px; text-align: center; border: 1px solid #d1fae5; }
        .card-value { font-size: 24px; font-weight: 700; color: #10b981; }
        .card-label { font-size: 12px; color: #6b7280; margin-top: 5px; }
        .comparativa { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
        .comparativa-col { background: #f9fafb; padding: 20px; border-radius: 12px; }
        .comparativa-col h3 { margin: 0 0 15px 0; font-size: 16px; color: #374151; }
        .kpi-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
        .kpi-label { color: #6b7280; }
        .kpi-value { font-weight: 700; }
        .good { color: #10b981; }
        .neutral { color: #f59e0b; }
        .opportunity { color: #ef4444; }
        .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px; }
        @media print { body { margin: 20px; } .hero { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>
      <div class="header">
        <div class="logo">🌿 MOBILSAFE</div>
        <div class="subtitle">Informe de Impacto Sostenible</div>
      </div>
      
      <div class="section">
        <div class="section-title">📋 Datos del Cliente</div>
        <p><strong>Empresa:</strong> ${clientData.empresa}</p>
        <p><strong>Contacto:</strong> ${clientData.contacto}</p>
        <p><strong>Sector:</strong> ${bench?.name || formData.sector}</p>
        <p><strong>Vehículos:</strong> ${formData.numVehicles}</p>
      </div>

      <div class="section">
        <div class="section-title">📊 Comparativa con el Sector</div>
        <p style="color: #6b7280; margin-bottom: 15px;">Basado en ${bench?.sampleSize || 0} flotas similares en España</p>
        <div class="comparativa">
          <div class="comparativa-col">
            <h3>🚛 Tu Flota</h3>
            <div class="kpi-row"><span class="kpi-label">⛽ Consumo</span><span class="kpi-value">${formData.fuelConsumption} L/100km</span></div>
            <div class="kpi-row"><span class="kpi-label">🚨 Accidentes</span><span class="kpi-value">${formData.accidentHoursPercent}%</span></div>
            <div class="kpi-row"><span class="kpi-label">🏥 Absentismo</span><span class="kpi-value">${formData.absenteeismPercent}%</span></div>
            <div class="kpi-row"><span class="kpi-label">🚦 Multas</span><span class="kpi-value">${formData.finesPerVehicleYear}/año</span></div>
            <div class="kpi-row"><span class="kpi-label">🔧 Mantenimiento</span><span class="kpi-value">${formData.maintenanceCostPerVehicle}€</span></div>
          </div>
          <div class="comparativa-col">
            <h3>📈 Media del Sector</h3>
            <div class="kpi-row"><span class="kpi-label">⛽ Consumo</span><span class="kpi-value">${bench?.consumo?.media || '-'} L/100km</span></div>
            <div class="kpi-row"><span class="kpi-label">🚨 Accidentes</span><span class="kpi-value">${bench?.accidentes?.media || '-'}%</span></div>
            <div class="kpi-row"><span class="kpi-label">🏥 Absentismo</span><span class="kpi-value">${bench?.absentismo?.media || '-'}%</span></div>
            <div class="kpi-row"><span class="kpi-label">🚦 Multas</span><span class="kpi-value">${bench?.multas?.media || '-'}/año</span></div>
            <div class="kpi-row"><span class="kpi-label">🔧 Mantenimiento</span><span class="kpi-value">${bench?.mantenimiento?.media || '-'}€</span></div>
          </div>
        </div>
        ${config.benchmarkSources ? `<p style="margin-top: 15px; padding: 12px; background: #f0fdf4; border-radius: 8px; font-size: 12px; color: #047857; border: 1px solid #d1fae5;"><strong>📄 Fuente:</strong> ${config.benchmarkSources}</p>` : ''}
      </div>

      ${diagnostico && diagnostico.problemas && Object.keys(diagnostico.problemas).length > 0 ? `
      <div class="section" style="page-break-before: always;">
        <div class="section-title">🔍 Diagnóstico Personalizado</div>
        ${diagnostico.resumen ? `<p style="padding: 15px; background: linear-gradient(135deg, #ecfeff, #e0f2fe); border-radius: 12px; border-left: 4px solid #06b6d4; margin-bottom: 20px; font-style: italic; color: #0e7490;">"${diagnostico.resumen}"</p>` : ''}
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
          ${Object.entries(diagnostico.problemas).map(([area, problemas]) => {
            if (!problemas || problemas.length === 0) return '';
            const areaNames = {
              seguridad: '🛡️ Seguridad y Riesgo',
              combustible: '⛽ Combustible',
              operativa: '📊 Operativa y Control',
              mantenimiento: '🔧 Mantenimiento',
              tic: '💻 TIC / Digitalización',
              sostenibilidad: '🌱 Sostenibilidad / ESG'
            };
            return `
              <div style="background: #f9fafb; padding: 15px; border-radius: 12px; border: 1px solid #e5e7eb;">
                <h4 style="margin: 0 0 12px 0; color: #10b981; font-size: 14px;">${areaNames[area] || area}</h4>
                ${problemas.slice(0, 3).map(item => `
                  <div style="margin-bottom: 10px; padding: 10px; background: white; border-radius: 8px; border: 1px solid #d1fae5;">
                    <p style="margin: 0 0 5px 0; color: #dc2626; font-size: 12px;">⚠️ ${item.problema}</p>
                    <p style="margin: 0; color: #059669; font-size: 12px;">✅ ${item.solucion}</p>
                  </div>
                `).join('')}
              </div>
            `;
          }).join('')}
        </div>
      </div>
      ` : ''}

      <div class="hero">
        <p style="font-size: 14px; opacity: 0.8; margin-bottom: 10px;">IMPACTO SOSTENIBLE PROYECTADO</p>
        <h1>${fmt(r.totalAnnualSavingEuros)} €</h1>
        <p>Ahorro Total Anual</p>
      </div>

      <div class="grid">
        <div class="card"><div class="card-value">${fmt(r.totalOperationalSavingEuros)} €</div><div class="card-label">Ahorro Operativo</div></div>
        <div class="card"><div class="card-value">${fmt(r.caesNeto)} €</div><div class="card-label">CAES Anual</div></div>
        <div class="card"><div class="card-value">${fmt(r.savingPerVehicle)} €</div><div class="card-label">€/vehículo/año</div></div>
      </div>

      <div class="section">
        <div class="section-title">Impacto Ambiental</div>
        <div class="grid">
          <div class="card"><div class="card-value">${fmt(r.co2ReductionTons)} ton</div><div class="card-label">CO2 Evitado</div></div>
          <div class="card"><div class="card-value">${fmt(r.litrosAhorrados)} L</div><div class="card-label">Litros Ahorrados</div></div>
          <div class="card"><div class="card-value">${fmt(r.mwhAhorrados)} MWh</div><div class="card-label">Energia Ahorrada</div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Desglose de Ahorros</div>
        <div class="kpi-row"><span class="kpi-label">Combustible</span><span class="kpi-value">${fmt(r.fuelSavingEuros)} €</span></div>
        <div class="kpi-row"><span class="kpi-label">Accidentes</span><span class="kpi-value">${fmt(r.accidentSavingEuros)} €</span></div>
        <div class="kpi-row"><span class="kpi-label">Bajas</span><span class="kpi-value">${fmt(r.absenteeismSavingEuros)} €</span></div>
        <div class="kpi-row"><span class="kpi-label">Multas</span><span class="kpi-value">${fmt(r.fineSavingEuros)} €</span></div>
        <div class="kpi-row"><span class="kpi-label">Mantenimiento</span><span class="kpi-value">${fmt(r.maintenanceSavingEuros)} €</span></div>
      </div>

      <div class="footer">
        <p>Simulación generada el ${new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        <p style="margin-top: 5px;">🌿 MOBILSAFE Fleet Technology - Comprometidos con la movilidad sostenible</p>
      </div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  if (!formData) return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center"><RefreshCw className="w-12 h-12 text-emerald-400 animate-spin" /></div>;

  const currentBench = config.benchmarks?.[formData.sector];

  // FORM PAGE
  if (page === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 text-white py-4 shadow-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-lg transition-all" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
              <MobilsafeLogoOnGreen />
              <div className="border-l border-white/30 pl-4"><h1 className="text-xl font-black">Análisis: {clientData.empresa}</h1><p className="text-emerald-200 text-sm">{currentBench?.icon} {currentBench?.name} • Paso 2 de 3</p></div>
            </div>
            <button onClick={calcularImpacto} disabled={loading} className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg font-bold shadow-lg transition-all flex items-center gap-2 disabled:opacity-50" data-testid="calculate-impact-btn">
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Leaf className="w-5 h-5" />} Calcular Impacto
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-2 border-emerald-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg"><Building2 className="w-7 h-7 text-white" /></div>
                <div><h2 className="text-2xl font-black text-gray-800">{clientData.empresa}</h2><p className="text-gray-600">{clientData.contacto}</p></div>
              </div>
              <div className="flex items-center gap-6 text-sm">
                {clientData.email && <span className="flex items-center gap-2 text-gray-600"><Mail className="w-4 h-4" /> {clientData.email}</span>}
                {clientData.telefono && <span className="flex items-center gap-2 text-gray-600"><Phone className="w-4 h-4" /> {clientData.telefono}</span>}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <FormColumn title="Datos de Flota" icon={<Truck />} color="emerald" formData={formData} handleChange={handleChange} fields={[
              { label: "Número de Vehículos", name: "numVehicles", type: "number" },
              { label: "Km/año por vehículo", name: "kmPerVehicleYear", type: "number" },
              { label: "Consumo (L/100km)", name: "fuelConsumption", type: "number", step: "0.1" },
              { label: "Precio combustible (€/L)", name: "fuelCostPerLiter", type: "number", step: "0.01" }
            ]} />
            <FormColumn title="KPI Operativos" icon={<BarChart3 />} color="amber" formData={formData} handleChange={handleChange} fields={[
              { label: "Horas perdidas accidentes (%)", name: "accidentHoursPercent", type: "number", step: "0.1", hint: "Típico: 0.5-2%" },
              { label: "Absentismo laboral (%)", name: "absenteeismPercent", type: "number", step: "0.1", hint: "Típico: 0.3-1%" },
              { label: "Multas por vehículo/año", name: "finesPerVehicleYear", type: "number", step: "0.1" },
              { label: "Coste medio multa (€)", name: "avgFineAmount", type: "number" },
              { label: "Coste mantenimiento/veh./año (€)", name: "maintenanceCostPerVehicle", type: "number", hint: "Típico: 2.000-4.000€" }
            ]} />
            <FormColumn title="Mejoras MOBILSAFE" icon={<Leaf />} color="green" formData={formData} handleChange={handleChange} fields={[
              { label: "Ahorro combustible (%)", name: "fuelSavingPercent", type: "number", step: "0.5", hint: "Recomendado: 3-8%" },
              { label: "Reducción accidentes (%)", name: "accidentReductionPercent", type: "number", step: "5", hint: "Recomendado: 20-40%" },
              { label: "Reducción bajas (%)", name: "absenteeismReductionPercent", type: "number", step: "5", hint: "Recomendado: 30-60%" },
              { label: "Reducción multas (%)", name: "fineReductionPercent", type: "number", step: "5", hint: "Recomendado: 15-35%" },
              { label: "Reducción mantenimiento (%)", name: "maintenanceReductionPercent", type: "number", step: "1", hint: "Recomendado: 10-15%" }
            ]} />
          </div>

          <div className="mt-8 text-center">
            <button onClick={calcularImpacto} disabled={loading} className="px-12 py-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white text-xl font-black rounded-xl shadow-2xl transition-all transform hover:scale-105 flex items-center gap-3 mx-auto disabled:opacity-50" data-testid="calculate-main-btn">
              {loading ? <RefreshCw className="w-6 h-6 animate-spin" /> : <Leaf className="w-6 h-6" />} Calcular Impacto Sostenible
            </button>
          </div>
        </div>
      </div>
    );
  }

  // COMPARATIVA PAGE
  if (page === 'comparativa') {
    const bench = config.benchmarks?.[formData.sector];
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 text-white py-4 shadow-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => setPage('form')} className="p-2 hover:bg-white/20 rounded-lg transition-all" data-testid="back-to-form-btn"><ArrowLeft className="w-5 h-5" /></button>
              <MobilsafeLogoOnGreen />
              <div className="border-l border-white/30 pl-4"><h1 className="text-xl font-black">Comparativa: {clientData.empresa}</h1><p className="text-emerald-200 text-sm">{bench?.icon} {bench?.name} • Paso 3 de 3</p></div>
            </div>
            <button onClick={() => setPage('proceso')} className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg font-bold shadow-lg transition-all flex items-center gap-2" data-testid="view-results-btn">
              Ver Impacto <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-black text-gray-800 mb-2">¿Cómo se compara {clientData.empresa}?</h2>
            <p className="text-gray-600">Basado en {bench?.sampleSize} flotas similares</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-emerald-200">
              <h3 className="text-2xl font-black text-gray-800 mb-6 flex items-center gap-3"><Truck className="w-8 h-8 text-emerald-600" /> Flota del Cliente</h3>
              <div className="space-y-4">
                <ComparativaItem label="Consumo" value={formData.fuelConsumption} unit="L/100km" bench={bench?.consumo} />
                <ComparativaItem label="Accidentes" value={formData.accidentHoursPercent} unit="%" bench={bench?.accidentes} />
                <ComparativaItem label="Absentismo" value={formData.absenteeismPercent} unit="%" bench={bench?.absentismo} />
                <ComparativaItem label="Multas" value={formData.finesPerVehicleYear} unit="/año" bench={bench?.multas} />
                <ComparativaItem label="Mantenimiento" value={formData.maintenanceCostPerVehicle} unit="€" bench={bench?.mantenimiento} />
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl shadow-xl p-6 border-2 border-emerald-300">
              <h3 className="text-2xl font-black text-gray-800 mb-6 flex items-center gap-3"><BarChart3 className="w-8 h-8 text-emerald-600" /> Media del Sector</h3>
              <div className="space-y-4">
                <MediaItem label="Consumo" value={bench?.consumo?.media} unit="L/100km" />
                <MediaItem label="Accidentes" value={bench?.accidentes?.media} unit="%" />
                <MediaItem label="Absentismo" value={bench?.absentismo?.media} unit="%" />
                <MediaItem label="Multas" value={bench?.multas?.media} unit="/año" />
                <MediaItem label="Mantenimiento" value={bench?.mantenimiento?.media} unit="€" />
              </div>
            </div>
          </div>

          {/* Fuentes de los benchmarks */}
          {config.benchmarkSources && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-8 text-center">
              <p className="text-sm text-emerald-700 flex items-center justify-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="font-medium">Fuente:</span> {config.benchmarkSources}
              </p>
            </div>
          )}

          <div className="text-center">
            <button onClick={() => setPage('proceso')} className="px-12 py-4 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white text-xl font-black rounded-xl shadow-2xl transition-all transform hover:scale-105 flex items-center gap-3 mx-auto" data-testid="view-impact-btn">
              <Leaf className="w-6 h-6" /> Ver Cómo lo Conseguimos
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PROCESO CAES PAGE
  if (page === 'proceso') {
    return (
      <ComoLoHacemos 
        clientData={clientData}
        onBack={() => setPage('comparativa')}
        onContinue={() => setPage('resultados')}
      />
    );
  }

  // RESULTS PAGE
  if (page === 'resultados' && resultado) {
    const max = Math.max(resultado.fuelSavingEuros, resultado.accidentSavingEuros, resultado.absenteeismSavingEuros, resultado.fineSavingEuros, resultado.maintenanceSavingEuros);

    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50" ref={printRef}>
        <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 text-white py-4 shadow-xl sticky top-0 z-50 no-print">
          <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => setPage('proceso')} className="p-2 hover:bg-white/20 rounded-lg transition-all" data-testid="back-to-comparativa-btn"><ArrowLeft className="w-5 h-5" /></button>
              <MobilsafeLogoOnGreen />
              <div className="border-l border-white/30 pl-4"><h1 className="text-xl font-black">Impacto: {clientData.empresa}</h1><p className="text-emerald-200 text-sm flex items-center gap-2"><Leaf className="w-4 h-4" /> Análisis completado</p></div>
            </div>
            <div className="flex gap-3">
              {!saved ? (
                <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold transition-all flex items-center gap-2 disabled:opacity-50" data-testid="save-simulation-btn">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
                </button>
              ) : (
                <span className="px-4 py-2 bg-green-500 rounded-lg font-semibold flex items-center gap-2"><Check className="w-4 h-4" /> Guardado</span>
              )}
              <button onClick={onBack} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all" data-testid="new-simulation-btn">Nueva</button>
              
              {/* PDF Dropdown */}
              <div className="relative">
                <button onClick={() => setShowPdfMenu(!showPdfMenu)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center gap-2" data-testid="pdf-menu-btn">
                  <Download className="w-4 h-4" /> PDF <ChevronDown className="w-4 h-4" />
                </button>
                {showPdfMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
                    <button onClick={handlePrintIndividual} className="w-full px-4 py-3 text-left text-gray-800 hover:bg-emerald-50 flex items-center gap-3 transition-all" data-testid="pdf-individual-btn">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <div><p className="font-semibold">PDF Individual</p><p className="text-xs text-gray-500">Solo resultados</p></div>
                    </button>
                    <button onClick={handlePrintComparativa} className="w-full px-4 py-3 text-left text-gray-800 hover:bg-emerald-50 flex items-center gap-3 transition-all border-t border-gray-100" data-testid="pdf-comparativa-btn">
                      <BarChart3 className="w-5 h-5 text-emerald-600" />
                      <div><p className="font-semibold">PDF con Comparativa</p><p className="text-xs text-gray-500">Resultados + sector</p></div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Hero */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl mb-8">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-green-500 to-teal-500"></div>
            <div className="relative px-12 py-12 text-white">
              <div className="text-center mb-8">
                <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-8 py-3 mb-6">
                  <p className="font-bold text-lg uppercase tracking-wider flex items-center gap-2 justify-center"><Leaf className="w-5 h-5" /> Impacto Sostenible: {clientData.empresa}</p>
                </div>
                <h1 className="text-7xl md:text-8xl font-black mb-4 drop-shadow-2xl" data-testid="total-savings">{resultado.totalAnnualSavingEuros.toLocaleString('es-ES')} €</h1>
                <p className="text-3xl font-semibold opacity-90">Ahorro Total Anual Proyectado</p>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="bg-white/15 backdrop-blur-md rounded-2xl p-6 text-center border border-white/30">
                  <p className="text-sm opacity-80 mb-2 uppercase">Ahorro Operativo</p>
                  <p className="text-4xl font-black">{resultado.totalOperationalSavingEuros.toLocaleString('es-ES')} €</p>
                </div>
                <div className="bg-white/15 backdrop-blur-md rounded-2xl p-6 text-center border border-white/30">
                  <p className="text-sm opacity-80 mb-2 uppercase">CAES Anual</p>
                  <p className="text-4xl font-black">{resultado.caesNeto.toLocaleString('es-ES')} €</p>
                  <p className="text-xs opacity-75 mt-1">Cobrable cada 5 años</p>
                </div>
                <div className="bg-white/15 backdrop-blur-md rounded-2xl p-6 text-center border border-white/30">
                  <p className="text-sm opacity-80 mb-2 uppercase">€/vehículo/año</p>
                  <p className="text-4xl font-black">{resultado.savingPerVehicle.toLocaleString('es-ES')} €</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-lime-400 to-green-400 rounded-2xl p-6 shadow-2xl">
                <div className="text-center mb-3">
                  <p className="text-gray-900 text-3xl font-black uppercase flex items-center justify-center gap-3"><Fuel className="w-8 h-8" /> {resultado.kmGratis.toLocaleString('es-ES')} KM SIN PAGAR COMBUSTIBLE</p>
                </div>
                <div className="grid grid-cols-3 gap-4 text-gray-800">
                  <div className="bg-white/50 rounded-xl p-3 text-center"><p className="text-2xl font-black">{resultado.vueltasMundo}</p><p className="text-sm font-bold">Vueltas al mundo</p></div>
                  <div className="bg-white/50 rounded-xl p-3 text-center"><p className="text-2xl font-black">{resultado.diasFlotaGratis}</p><p className="text-sm font-bold">Días flota gratis</p></div>
                  <div className="bg-white/50 rounded-xl p-3 text-center"><p className="text-2xl font-black">{resultado.litrosAhorrados.toLocaleString('es-ES')} L</p><p className="text-sm font-bold">Litros ahorrados</p></div>
                </div>
              </div>
            </div>
          </div>

          {/* Sección de Diagnóstico (solo si hay análisis consultivo) */}
          {diagnostico && diagnostico.problemas && Object.keys(diagnostico.problemas).length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border-2 border-cyan-200">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-cyan-100">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-gray-800">Diagnóstico Personalizado</h3>
                  <p className="text-sm text-gray-500">Problemas detectados y soluciones MOBILSAFE</p>
                </div>
              </div>
              
              {/* Resumen IA */}
              {diagnostico.resumen && (
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 mb-6 border border-cyan-200">
                  <p className="text-gray-700 italic">"{diagnostico.resumen}"</p>
                </div>
              )}
              
              {/* Problemas por área */}
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(diagnostico.problemas).map(([area, problemas]) => {
                  if (!problemas || problemas.length === 0) return null;
                  const areaNames = {
                    seguridad: { name: 'Seguridad y Riesgo', icon: <Shield className="w-5 h-5" /> },
                    combustible: { name: 'Combustible', icon: <Fuel className="w-5 h-5" /> },
                    operativa: { name: 'Operativa y Control', icon: <Gauge className="w-5 h-5" /> },
                    mantenimiento: { name: 'Mantenimiento', icon: <Wrench className="w-5 h-5" /> },
                    tic: { name: 'TIC / Digitalización', icon: <Monitor className="w-5 h-5" /> },
                    sostenibilidad: { name: 'Sostenibilidad / ESG', icon: <TreePine className="w-5 h-5" /> }
                  };
                  const areaInfo = areaNames[area] || { name: area, icon: <AlertTriangle className="w-5 h-5" /> };
                  
                  return (
                    <div key={area} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white">
                          {areaInfo.icon}
                        </div>
                        <h4 className="font-bold text-gray-800">{areaInfo.name}</h4>
                      </div>
                      <div className="space-y-2">
                        {problemas.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-emerald-100">
                            <p className="text-sm text-red-600 font-medium flex items-start gap-1">
                              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {item.problema}
                            </p>
                            <p className="text-sm text-emerald-600 font-medium flex items-start gap-1 mt-1">
                              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {item.solucion}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-xl p-6 border-t-4 border-emerald-500">
              <h3 className="text-xl font-black text-gray-800 mb-4 flex items-center gap-2"><Euro className="w-6 h-6 text-emerald-600" /> Ahorros Operativos</h3>
              <div className="space-y-3">
                <SavingBar label="Combustible" value={resultado.fuelSavingEuros} max={max} color="from-amber-400 to-orange-500" />
                <SavingBar label="Accidentes" value={resultado.accidentSavingEuros} max={max} color="from-red-400 to-pink-500" />
                <SavingBar label="Bajas" value={resultado.absenteeismSavingEuros} max={max} color="from-purple-400 to-purple-600" />
                <SavingBar label="Multas" value={resultado.fineSavingEuros} max={max} color="from-yellow-400 to-amber-500" />
                <SavingBar label="Mantenimiento" value={resultado.maintenanceSavingEuros} max={max} color="from-blue-400 to-blue-600" />
              </div>
              <div className="mt-4 pt-4 border-t-2 text-center"><p className="text-sm font-bold text-gray-600 uppercase">Total</p><p className="text-3xl font-black text-emerald-600">{resultado.totalOperationalSavingEuros.toLocaleString('es-ES')} €</p></div>
            </div>

            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="text-xl font-black mb-4 flex items-center gap-2"><Zap className="w-6 h-6" /> CAES</h3>
              <div className="space-y-3">
                <div className="bg-white/20 rounded-xl p-4"><p className="text-sm opacity-90 mb-1">Valor Anual</p><p className="text-3xl font-black">{resultado.caesNeto.toLocaleString('es-ES')} €</p></div>
                <div className="bg-white/20 rounded-xl p-4"><p className="text-sm opacity-90 mb-1">Energía Ahorrada</p><p className="text-3xl font-black">{resultado.mwhAhorrados.toLocaleString('es-ES')} MWh</p></div>
                <div className="bg-white/20 rounded-xl p-4"><p className="text-sm opacity-90 mb-1">Litros Ahorrados</p><p className="text-2xl font-black">{resultado.litrosAhorrados.toLocaleString('es-ES')} L</p></div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-600 to-teal-600 rounded-2xl shadow-xl p-6 text-white">
              <h3 className="text-xl font-black mb-4 flex items-center gap-2"><Leaf className="w-6 h-6" /> Impacto Ambiental</h3>
              <div className="space-y-3">
                <div className="bg-white/20 rounded-xl p-4"><p className="text-sm opacity-90 mb-1">Flota</p><p className="text-3xl font-black">{formData.numVehicles} vehículos</p></div>
                <div className="bg-white/20 rounded-xl p-4 border-2 border-white/30"><p className="text-sm opacity-90 mb-1">CO₂ Evitado</p><p className="text-4xl font-black">{resultado.co2ReductionTons.toLocaleString('es-ES')} ton</p></div>
                <div className="bg-white/20 rounded-xl p-4"><p className="text-sm opacity-90 mb-1">Equivalente en Árboles</p><p className="text-3xl font-black">{(resultado.co2ReductionTons * 50).toLocaleString('es-ES')}</p></div>
              </div>
            </div>
          </div>

          {/* Fuentes de los benchmarks para impresión */}
          {config.benchmarkSources && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-6 print-only">
              <p className="text-sm text-emerald-700 text-center">
                <span className="font-medium">Fuente de datos:</span> {config.benchmarkSources}
              </p>
            </div>
          )}

          <div className="text-center text-gray-500 text-sm mt-8">
            <p>Simulación generada el {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="mt-2 font-semibold text-emerald-600 flex items-center justify-center gap-2"><Leaf className="w-4 h-4" /> MOBILSAFE - Movilidad Sostenible</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Helper Components
const FormColumn = ({ title, icon, color, formData, handleChange, fields }) => (
  <div className={`bg-white rounded-2xl shadow-lg p-6 border-l-4 border-${color}-500`}>
    <div className={`flex items-center gap-3 mb-6 pb-4 border-b-2 border-${color}-100`}>
      <div className={`w-12 h-12 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-xl flex items-center justify-center shadow-lg text-white`}>{icon}</div>
      <h3 className="text-lg font-black text-gray-800">{title}</h3>
    </div>
    <div className="space-y-4">
      {fields.map((field) => (
        <div key={field.name}>
          <label className="block text-sm font-bold text-gray-700 mb-2">{field.label}</label>
          <input type={field.type} name={field.name} value={formData[field.name]} onChange={handleChange} step={field.step} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all text-center font-semibold" data-testid={`input-${field.name}`} />
          {field.hint && <p className="text-xs text-gray-500 mt-1">{field.hint}</p>}
        </div>
      ))}
    </div>
  </div>
);

const ComparativaItem = ({ label, value, unit, bench }) => {
  if (!bench) return null;
  let status = { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-300', text: 'En la media' };
  if (value <= bench.p25) status = { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'Top 25%' };
  else if (value > bench.p75) status = { color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-300', text: 'Oportunidad' };
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${status.bg} ${status.border}`}>
      <div><p className="font-bold text-gray-800">{label}</p><p className="text-2xl font-black text-gray-900">{value} {unit}</p></div>
      <p className={`text-xs font-bold ${status.color}`}>{status.text}</p>
    </div>
  );
};

const MediaItem = ({ label, value, unit }) => (
  <div className="flex items-center justify-between p-3 rounded-lg bg-white/70">
    <p className="font-bold text-gray-700">{label}</p>
    <p className="text-2xl font-black text-emerald-600">{value} {unit}</p>
  </div>
);

const SavingBar = ({ label, value, max, color }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-bold text-gray-700">{label}</span>
        <span className="text-sm font-black text-gray-800">{value.toLocaleString('es-ES')} €</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-1000`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

export default Simulator;
